<template>
  <div class="header d-flex">
    <div class="header-container d-flex">
      <div class="right-nav">
        <AppLogo
          dark
          :logo-url="orgLogoUrl"
          :org-name="orgDisplayName"
          class="cursor-pointer"
          @click="goto('/lms')"
        />
      </div>
      <div class="left-nav d-none d-lg-flex">
        <router-link
          class="router-link cursor-pointer me-3"
          to="/lms"
          v-if="notOnboarding"
        >
          Meine Aufgaben
        </router-link>

        <router-link
          class="router-link cursor-pointer"
          v-if="isAdmin && notOnboarding"
          to="/company"
        >
          Administration
        </router-link>

        <div ref="userMenu" class="user-menu">
          <button
            class="user-trigger"
            type="button"
            aria-label="Benutzermenü öffnen"
            :aria-expanded="showUserMenu"
            @click="toggleUserMenu"
          >
            <span class="user-avatar" aria-hidden="true">{{ initials }}</span>
            <span class="user-trigger-text">
              <span class="user-trigger-name">{{ displayName }}</span>
              <span v-if="displayName && userEmail" class="user-trigger-email">{{
                userEmail
              }}</span>
            </span>
            <vue-feather
              type="chevron-down"
              size="18px"
              stroke="#ffffff"
              class="user-trigger-chevron"
              :class="{ open: showUserMenu }"
            ></vue-feather>
          </button>
          <Transition name="slide-fade">
            <div v-if="showUserMenu" class="dropdown">
              <button class="dropdown-item" type="button" @click="gotoAndCloseMenu('/einstellungen')">
                Persönliche Daten
              </button>
              <button
                v-if="hasMultipleOrgs"
                class="dropdown-item"
                type="button"
                @click="gotoAndCloseMenu('/organisationen')"
              >
                Organisation wechseln
              </button>
              <button
                v-if="isSuperAdmin && notOnboarding"
                class="dropdown-item"
                type="button"
                @click="gotoAndCloseMenu('/superadmin')"
              >
                Super-Administration
              </button>
              <div class="dropdown-divider"></div>
              <button class="dropdown-item" type="button" @click="logout">Abmelden</button>
            </div>
          </Transition>
        </div>
      </div>
      <div
        ref="menu"
        class="burger-btn d-block d-lg-none ms-auto"
        @click="toggleMenu"
      >
        <span class="icon-bar"></span>
        <span class="icon-bar"></span>
        <span class="icon-bar"></span>
      </div>
    </div>
    <!-- Header for mobile screens Start -->
    <Transition name="slide-fade">
      <div v-if="showMenu" class="menu d-flex d-lg-none">
        <div class="d-flex w-100 justify-content-between align-items-center">
          <button
            @click="goto('/lms')"
            class="btn-outline btn-small"
            v-if="notOnboarding"
          >
            Meine Aufgaben
          </button>

          <button
            @click="goto('/company')"
            class="btn-outline btn-small"
            v-if="isAdmin && notOnboarding"
          >
            Administration
          </button>

          <button
            @click="goto('/superadmin')"
            class="btn-outline btn-small"
            v-if="isSuperAdmin && notOnboarding"
          >
            Super-Administration
          </button>
        </div>
        <div
          class="d-flex w-100 pt-3 justify-content-between align-items-center"
          v-if="store.getters.getUser"
        >
          <div class="user-name">
            {{ store.getters.getUser.first_name }}
            {{ store.getters.getUser.last_name }}
            <a @click="goto('/einstellungen')" class="cursor-pointer">
              Einstellungen
            </a>
            <a @click="logout" class="cursor-pointer">
              Abmelden
            </a>
          </div>
          <div
            v-if="store.getters['organisation/getOrganisation']"
            class="user-name"
          >
            {{ store.getters["organisation/getOrganisation"].name }}
            <a
              v-if="hasMultipleOrgs"
              @click="goto('/organisationen')"
              class="cursor-pointer"
            >
              wechseln
            </a>
          </div>
        </div>
      </div>
    </Transition>
    <!-- Header for mobile screens End -->
  </div>
</template>
<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { Auth } from "aws-amplify";
import { store } from "@/store";
import { useRouter, useRoute } from "vue-router";
import AppLogo from "@/components/Header/AppLogo.vue";

const route = useRoute();
const router = useRouter();
const menu = ref(null);
const userMenu = ref(null);
const showMenu = ref(false);
const showUserMenu = ref(false);
const isAdmin = computed(() => store.getters["permission/isAdmin"]);
const isSuperAdmin = computed(() => store.getters["superAdmin/isSuperAdmin"]);
const notOnboarding = computed(
  () => route.name !== "onboarding" && route.name !== "company/create"
);
const hasMultipleOrgs = computed(() => {
  const all = store.getters["organisation/getAllOrganisation"];
  return Array.isArray(all) && all.length > 1;
});
// White-Label: hat die Organisation ein Logo hochgeladen (Firmen-Einstellungen),
// zeigt der Header Logo + Org-Name, sonst die MEDA-Marke. Bis die Org vom
// Server geladen ist, kommt das Branding aus dem localStorage-Cache, damit
// beim Boot nicht erst MEDA aufblitzt und dann umspringt.
const cachedBranding = (() => {
  try {
    return JSON.parse(localStorage.getItem("orgBranding") || "null");
  } catch {
    return null;
  }
})();
const organisation = computed(
  () => store.getters["organisation/getOrganisation"]
);
const orgLogoUrl = computed(() =>
  organisation.value
    ? organisation.value.data?.logoUrl ?? ""
    : cachedBranding?.logoUrl ?? ""
);
const orgDisplayName = computed(
  () =>
    (organisation.value ? organisation.value.name : cachedBranding?.name) ?? ""
);
const userEmail = computed(
  () => store.getters.getUserSession?.attributes?.email ?? ""
);
const displayName = computed(() => {
  const user = store.getters.getUser;
  const name = user
    ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
    : "";
  return name || userEmail.value;
});
const initials = computed(() => {
  const user = store.getters.getUser;
  const first = (user?.first_name ?? "").trim();
  const last = (user?.last_name ?? "").trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  return userEmail.value.charAt(0).toUpperCase();
});

// Mitgliedschaften laden (gecacht), damit „Organisation wechseln" erscheinen kann
watch(
  () => store.getters.getJWT,
  (jwt) => {
    if (jwt) {
      store.dispatch("organisation/FETCH_ALL").catch(() => undefined);
    }
  },
  { immediate: true }
);

function goto(path) {
  router.push({ path: path });
}
function gotoAndCloseMenu(path) {
  showUserMenu.value = false;
  router.push({ path: path });
}
function toggleMenu() {
  showMenu.value = !showMenu.value;
}
function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value;
}
function logout() {
  showUserMenu.value = false;
  localStorage.removeItem("organisationId");
  localStorage.removeItem("orgBranding");
  Auth.signOut();
}
function clickOutSide(event) {
  if (
    menu.value &&
    !(menu.value === event.target || menu.value.contains(event.target))
  ) {
    showMenu.value = false;
  }
  if (
    userMenu.value &&
    !(userMenu.value === event.target || userMenu.value.contains(event.target))
  ) {
    showUserMenu.value = false;
  }
}

onMounted(() => {
  document.body.addEventListener("click", clickOutSide);
});

onUnmounted(() => {
  document.body.removeEventListener("click", clickOutSide);
});
</script>
<style lang="scss" scoped>
.header {
  position: fixed;
  top: 0px;
  z-index: 100;
  height: 77px;
  width: 100%;
  background: $primary-deep;
  box-shadow: 0 4px 20px rgba(10, 57, 91, 0.25);
  border-radius: 0;
  flex-direction: column;
  &-container {
    height: fit-content;
    /* horizontal am .wrapper (1180 + 24px) ausgerichtet, damit Logo unter dem
       Seiteninhalt (z. B. "AWO Hamburg") bündig sitzt */
    padding: 15px 24px;
    display: flex;
    width: 100%;
    max-width: 1180px;
    margin: 0 auto;
    align-items: center;
    .right-nav {
      display: flex;
      align-items: center;
      .divider {
        height: 35px;
        opacity: 0.2;
        border: 1px solid $primary;
        margin: 0px 40px 0px 20px;
      }
    }
    .left-nav {
      margin-left: auto;
      display: flex;
      justify-content: space-between;
      font-weight: 500;
      align-items: center;
      flex-direction: row;
      .facility-setting {
        margin-right: 30px;
        font-size: 17px;
        line-height: 26px;
        text-decoration-line: underline;
        color: $primary;
      }
      .user-name {
        font-size: 18px;
        line-height: 28px;
        color: $ink;
      }
      .company-name {
        font-weight: 400;
        font-size: 16px;
        line-height: 22px;
        color: $muted;
      }
      .router-link {
        font-family: 'Poppins', ui-sans-serif, system-ui, sans-serif;
        font-size: 15px;
        font-weight: 500;
        letter-spacing: -0.1px;
        color: #fff;
        text-decoration: none;
        position: relative;
        padding-bottom: 2px;
        transition: color 150ms ease;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: $primary-bright;
          border-radius: 2px;
          transition: width 150ms ease;
        }
        &:hover {
          color: $primary-bright;
          &::after { width: 100%; }
        }
        &.router-link-active {
          color: #fff;
          font-weight: 600;
          &::after { width: 100%; }
        }
      }
      .user-menu {
        position: relative;
        // dezente Trennlinie zur Navigation, damit der Block ruhig absetzt
        margin-left: 28px;
        padding-left: 28px;
        border-left: 1px solid rgba(255, 255, 255, 0.2);
      }
      .user-trigger {
        display: flex;
        align-items: center;
        gap: 10px;
        // globale button{}-Regel (min-width/min-height/padding) zurücksetzen
        min-width: 0;
        min-height: 44px;
        margin: 0;
        padding: 4px 10px;
        background: transparent;
        border: none;
        box-shadow: none;
        border-radius: 8px;
        text-align: left;
        cursor: pointer;
        transition: background-color 150ms ease;
        &:hover {
          background-color: rgba(255, 255, 255, 0.12);
        }
        &:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px $primary-bright;
        }
      }
      .user-avatar {
        flex-shrink: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: $primary-bright;
        font-family: 'Poppins', ui-sans-serif, system-ui, sans-serif;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.5px;
        color: #fff;
      }
      .user-trigger-text {
        display: flex;
        flex-direction: column;
      }
      .user-trigger-name {
        font-family: 'Poppins', ui-sans-serif, system-ui, sans-serif;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        color: #fff;
      }
      .user-trigger-email {
        font-size: 12px;
        line-height: 16px;
        font-weight: 400;
        color: rgba(255, 255, 255, 0.7);
      }
      .user-trigger-chevron {
        flex-shrink: 0;
        transition: transform 150ms ease;
        &.open {
          transform: rotate(180deg);
        }
      }
      .dropdown {
        position: absolute;
        top: calc(100% + 10px);
        right: 0;
        min-width: 230px;
        background: $white;
        border: 1px solid $line;
        border-radius: 10px;
        box-shadow: 0 6px 24px rgba(22, 40, 58, 0.15);
        padding: 8px 0;
        z-index: 200;
        .dropdown-item {
          display: block;
          width: 100%;
          text-align: left;
          // globale button{}-Regel zurücksetzen
          min-width: 0;
          min-height: 44px;
          box-shadow: none;
          margin: 0;
          padding: 10px 16px;
          background: transparent;
          border: none;
          border-radius: 0;
          font-size: 14px;
          font-weight: 500;
          color: $ink;
          cursor: pointer;
          transition: background-color 150ms ease;
          &:hover {
            background: $tint;
          }
        }
        .dropdown-divider {
          height: 1px;
          background: $line;
          margin: 6px 0;
        }
      }
    }
  }
}
.burger-btn {
  background: $primary;
  height: fit-content;
  width: fit-content;
  padding: 10px 15px;
  border-radius: 8px;
  margin: 0px 10px;
  cursor: pointer;
  transition: background-color 150ms ease;
  &:hover {
    background-color: $primary-bright;
  }
  .icon-bar {
    border-radius: 10px;
    margin-bottom: 3px;
    height: 4px;
    width: 20px;
    background: $white;
    display: block;
  }
  .icon-bar:nth-child(3) {
    margin-bottom: 0px;
  }
}
.menu {
  z-index: 100;
  padding: 15px;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;
  background: $white;
  width: 100%;
  border-top: 1px solid $line;
  .user-name {
    font-size: 14px;
    line-height: 22px;
    color: $ink;
    padding-bottom: 2px;
  }
  .company-name {
    font-weight: 400;
    font-size: 12px;
    line-height: 22px;
    color: $muted;
  }
}
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
  opacity: 0.5;
}
.slide-fade-leave-active {
  transition: all 0.3s ease-out;
  opacity: 1;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

@media (min-width: 1300) {
  .wrapper {
    max-width: 1500px;
  }
}
@media (max-width: 998px) {
  .header {
    &-container {
      max-width: 100%;
      padding-left: 16px;
      padding-right: 16px;
    }
    .right-nav {
      .divider {
        margin: 0 10px;
      }
    }
    .left-nav {
      margin-left: auto;
    }
  }
}
</style>
